import type { DatadogLogStatus, LogPayload, StructuredLogInput } from "./types";

import { isRecord } from "../object/is-record";
import { convertErrorForLogging } from "./convert-error-for-logging";

function isStructuredLogInput(value: unknown): value is StructuredLogInput {
  return (
    isRecord(value) &&
    !(value instanceof Error) &&
    typeof value.message === "string"
  );
}

function isTagValue(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function getSanitizedTags(
  value: unknown,
): Record<string, string | number | boolean> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const tags: Record<string, string | number | boolean> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isTagValue(entry)) {
      tags[key] = entry;
    }
  }

  return Object.keys(tags).length > 0 ? tags : undefined;
}

function getTagsFromDetails(
  details: Record<string, unknown> | undefined,
): Record<string, string | number | boolean> | undefined {
  if (!details) {
    return undefined;
  }

  return getSanitizedTags(details.tags);
}

function getExtraDataFromDetails(
  details: Record<string, unknown> | undefined,
): Record<string, unknown> {
  if (!details) {
    return {};
  }

  const extraData: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(details)) {
    if (key === "tags") {
      continue;
    }

    if (key === "data" && isRecord(value)) {
      Object.assign(extraData, value);
      continue;
    }

    extraData[key] = value;
  }

  return extraData;
}

function getSafeErrorDetails(error: unknown): unknown {
  const converted = convertErrorForLogging(error);

  if (
    typeof converted === "string" ||
    typeof converted === "number" ||
    typeof converted === "boolean"
  ) {
    return converted;
  }

  if (!isRecord(converted)) {
    return "Non-serializable error value omitted";
  }

  const safeDetails: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(converted)) {
    if (isTagValue(value)) {
      safeDetails[key] = value;
    }
  }

  return Object.keys(safeDetails).length > 0
    ? safeDetails
    : "Non-serializable error value omitted";
}

function buildErrorData(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      error_message: error.message,
      error_stack: error.stack,
    };
  }

  if (error !== undefined) {
    return { error_details: getSafeErrorDetails(error) };
  }

  return {};
}

function normalizeStructuredLog(
  input: StructuredLogInput,
  defaultLevel: DatadogLogStatus,
): LogPayload {
  const details = isRecord(input.details) ? input.details : undefined;
  const tags = getSanitizedTags(input.tags) ?? getTagsFromDetails(details);
  const data = {
    ...getExtraDataFromDetails(details),
    ...(input.data ?? {}),
    ...buildErrorData(input.error),
  };

  return {
    level: input.level ?? defaultLevel,
    message: input.message,
    ...(input.service ? { service: input.service } : {}),
    ...(tags ? { tags } : {}),
    ...(Object.keys(data).length > 0 ? { data } : {}),
  };
}

export { isStructuredLogInput, normalizeStructuredLog };
